import uniqueId from 'lodash/uniqueId';
import React, { useEffect, useMemo } from 'react';

import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { ROUTES } from 'utils/routes';
import { SeoCMS } from 'utils/sharedStrapiQueries/sharedTypes';
import {
  Award,
  CMSHeadlines,
  CMSInfobox,
  CMSLeadershipSection,
  ComponentPropsWithTypename,
} from 'utils/sharedStrapiQueries/sharedTypes';

import AwardsSection from './sections/AwardsSection';
import CapabilitiesSection, {
  CapabilitiesSectionProps,
} from './sections/CapabilitiesSection';
import ClientsSection from './sections/ClientsSection';
import DoubleColumnTextSection from './sections/DoubleColumnTextSection';
import ExpertiseSection from './sections/ExpertiseSection';
import InfoBoxesSection from './sections/InfoboxesSection';
import LeadershipSection from './sections/LeadershipSection';
import OfficeSection from './sections/OfficeSection';
import SocialMediaSection from './sections/SocialMediaSection';
import TextContentSection from './sections/TextContentSection';

import * as S from './AboutPage.styles';

const MODULES = {
  ComponentSectionsHero: S.HeroSection,
  ComponentSectionsHeadlineSection: S.AboutPageHeadline,
  ComponentSectionsInfoBoxesSection: InfoBoxesSection,
  ComponentSectionsLeadershipSection: LeadershipSection,
  ComponentSectionsSocialMediaSection: SocialMediaSection,
  ComponentSectionsClientsSection: ClientsSection,
  ComponentSectionsAwardsSection: AwardsSection,
  ComponentSectionsExpertiseSection: ExpertiseSection,
  ComponentSectionsOfficesCarouselSection: OfficeSection,
  ComponentSectionsCapabilitiesSection: CapabilitiesSection,
  ComponentSectionsTextContentSection: TextContentSection,
  ComponentSectionsDoubleColumnTextSection: DoubleColumnTextSection,
};

const OVERFLOW_NOT_HIDDEN = [
  'ComponentSectionsCapabilitiesSection',
  'ComponentSectionsClientsSection',
];

interface AboutPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: {
    modules: Array<
      | ComponentPropsWithTypename<CMSHeadlines>
      | ComponentPropsWithTypename<CMSInfobox>
      | ComponentPropsWithTypename<CMSLeadershipSection>
      | ComponentPropsWithTypename<CapabilitiesSectionProps>
    >;
    seo: SeoCMS;
  };
  awards: Array<Award>;
}

const AboutPage: React.FunctionComponent<AboutPageProps> = ({
  initialCopy,
  page,
  awards,
}) => {
  const { setCopy } = useCopyStore();
  const { setHeaderText } = useGlobalStore();

  useEffect(() => {
    setCopy(initialCopy);

    setHeaderText({
      default: initialCopy.navbar.navLinks.find(
        ({ href }) => href === ROUTES.ABOUT
      )?.headerText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const renderModules = useMemo(
    () =>
      page.modules.map((module, index) => {
        const Component = MODULES[module.__typename];
        if (!Component) return null;

        const wrap = !OVERFLOW_NOT_HIDDEN.includes(module.__typename);

        // find index for question section
        if (module.__typename === 'ComponentSectionsCapabilitiesSection') {
          const firstQuestionIndex = page.modules.findIndex(
            e => e.__typename === 'ComponentSectionsCapabilitiesSection'
          );

          const questionIndex = index - firstQuestionIndex + 1;

          // @ts-ignore
          module.upperHeading = `0${questionIndex}.`;
        }

        if (module.__typename === 'ComponentSectionsHeadlineSection') {
          // @ts-ignore
          module.renderTextAs = 'h1';
          // @ts-ignore
          module.size = 'heading1';
        }

        return (
          <S.AboutPageSectionWrapper
            lockWidth={wrap}
            key={uniqueId(module.__typename)}
          >
            <Component {...module} awards={awards} />
          </S.AboutPageSectionWrapper>
        );
      }),
    [page]
  );

  return <>{renderModules}</>;
};

export default AboutPage;
